import * as Sentry from "@sentry/react";
import { useEffect, useRef, useState } from "react";
import realtimeClient from "../../../realtime/client";
import {
  AUDIO_UPLOAD_CHUNK_CUT_WINDOW_START_MS,
  AUDIO_UPLOAD_CHUNK_TARGET_MS,
  AUDIO_UPLOAD_MAX_CONCURRENCY,
  CLINICAL_NOTE_POLL_DELAY_MS,
  CLINICAL_NOTE_POLL_INTERVAL_MS,
  CLINICAL_NOTE_POLL_TIMEOUT_MS,
  RECORDER_STATE,
  RECORDER_TIMESLICE_MS,
} from "./constants";
import {
  createAudioProcessingGraph,
  isAudioRecordingSupported,
  resumeAudioContextIfNeeded,
  stopMediaRecorder,
} from "./mediaRecorder";
import {
  fetchClinicalNote,
  resetClinicalNoteAudio,
  summarizeClinicalNote,
  uploadAudioChunk,
} from "./service";
import useAudioLevelMonitor from "./useAudioLevelMonitor";
import useElapsedTimer from "./useElapsedTimer";
import {
  buildAudioChunkFile,
  buildCombinedAudioChunkBlob,
  findQuietAudioCutDurationMs,
  getAudioIntensity,
  getBufferedDurationMs,
  getPreferredMimeType,
  getRecorderStatusMeta,
  splitBufferedAudioAtDuration,
} from "./utils";

const MICROPHONE_CONSTRAINTS = {
  audio: {
    autoGainControl: true,
    echoCancellation: true,
    noiseSuppression: true,
  },
};

export const SUMMARY_FAILURE_MESSAGE = "เกิดข้อผิดพลาด โปรดลองใหม่อีกครั้ง";
export const MICROPHONE_PERMISSION_MESSAGE =
  "ไม่สามารถใช้งานไมโครโฟนได้ กรุณาอนุญาตสิทธิ์ไมโครโฟนในคอมพิวเตอร์และเบราว์เซอร์ แล้วลองใหม่อีกครั้ง";
export const MICROPHONE_UNAVAILABLE_MESSAGE =
  "ไม่พบไมโครโฟน หรือไมโครโฟนไม่พร้อมใช้งาน กรุณาตรวจสอบอุปกรณ์แล้วลองใหม่อีกครั้ง";

function normalizeTreatmentId(treatmentId) {
  const numericTreatmentId = Number.parseInt(treatmentId, 10);
  return Number.isFinite(numericTreatmentId) ? numericTreatmentId : null;
}

function normalizeRecorderErrorDetails(error) {
  return {
    detail: String(error?.detail || "").trim(),
    message: String(error?.message || "").trim(),
    name: String(error?.name || "").trim(),
  };
}

function resolveRecorderUserMessage(error) {
  const { detail, message, name } = normalizeRecorderErrorDetails(error);
  const combinedMessage = `${detail} ${message}`.toLowerCase();

  if (
    name === "NotAllowedError" ||
    name === "PermissionDeniedError" ||
    name === "SecurityError" ||
    combinedMessage.includes("permission denied") ||
    combinedMessage.includes("notallowederror") ||
    combinedMessage.includes("not allowed") ||
    combinedMessage.includes("permission") ||
    combinedMessage.includes("denied") ||
    combinedMessage.includes("error starting the mediarecorder")
  ) {
    return MICROPHONE_PERMISSION_MESSAGE;
  }

  if (
    name === "NotFoundError" ||
    name === "DevicesNotFoundError" ||
    name === "NotReadableError" ||
    name === "TrackStartError" ||
    name === "AbortError" ||
    combinedMessage.includes("device not found") ||
    combinedMessage.includes("could not start audio source") ||
    combinedMessage.includes("device is in use") ||
    combinedMessage.includes("hardware error") ||
    combinedMessage.includes("microphone")
  ) {
    return MICROPHONE_UNAVAILABLE_MESSAGE;
  }

  return SUMMARY_FAILURE_MESSAGE;
}

export default function useAudioRecorder() {
  const [recorderState, setRecorderState] = useState(RECORDER_STATE.IDLE);
  const [errorMessage, setErrorMessage] = useState("");
  const [clinicalNoteSettings, setClinicalNoteSettings] = useState(null);
  const [latestSummaryResult, setLatestSummaryResult] = useState(null);

  const isMountedRef = useRef(false);
  const recorderStateRef = useRef(RECORDER_STATE.IDLE);
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceNodeRef = useRef(null);

  const recorderRunIdRef = useRef(0);
  const mimeTypeRef = useRef("");
  // bufferedAudioChunksRef เก็บเสียงที่ยังไม่ถูกตัดเป็น upload chunk
  const bufferedAudioChunksRef = useRef([]);
  const currentSegmentStartedAtRef = useRef(0);
  const chunkOrderRef = useRef(1);
  // queue นี้ใช้ทั้ง rolling upload, pause, และ final chunk โดย retry task เดิมได้
  const pendingUploadTasksRef = useRef([]);
  const pendingFinalizeChunkRef = useRef(null);
  const pendingDirectSummarizeRef = useRef(false);
  const activeUploadCountRef = useRef(0);
  const uploadQueuePausedRef = useRef(false);
  const uploadProcessorPromiseRef = useRef(null);
  const bufferedAudioMutationPromiseRef = useRef(Promise.resolve());
  const activeTreatmentIdRef = useRef(null);
  const pollingStartTimeoutRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const pollingTimeoutRef = useRef(null);
  const isPollingInFlightRef = useRef(false);
  const recorderRotationTimeoutRef = useRef(null);
  const recorderStopModeRef = useRef("idle");
  const rotationPromiseRef = useRef(null);
  const rotationPromiseRunIdRef = useRef(0);
  const rotationPromiseResolveRef = useRef(null);
  const cleanupRecordingSessionRef = useRef(async () => {});
  const pendingRestartResetPromiseRef = useRef(null);
  const pendingRestartResetRunIdRef = useRef(0);
  const pendingRecordingProgressRestorePromiseRef = useRef(null);
  const pendingRecordingProgressRestoreRunIdRef = useRef(0);
  const activeSummaryResolutionRunIdRef = useRef(0);
  const summaryResolutionSourceRef = useRef("");
  const uploadedDurationSecondsRef = useRef(0);

  const {
    elapsedSeconds,
    pauseElapsedTimer,
    resetElapsedTimer,
    restoreElapsedTimer,
    resumeElapsedTimer,
  } = useElapsedTimer();
  const {
    audioLevel,
    consumeAudioLevelSampleBatchUntil,
    isInRecordingGracePeriod,
    startAudioLevelMonitoring,
    stopAudioLevelMonitoring,
  } = useAudioLevelMonitor();

  const audioIntensity = getAudioIntensity(audioLevel);
  const recorderStatusMeta = getRecorderStatusMeta(
    recorderState,
    errorMessage,
    isInRecordingGracePeriod ? 1 : audioLevel,
  );

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      recorderRunIdRef.current += 1;
      clearSummaryPolling();
      void cleanupRecordingSessionRef.current();
    };
  }, []);

  useEffect(() => {
    async function handleVisibilityChange() {
      if (document.visibilityState !== "visible") {
        return;
      }

      const audioContext = audioContextRef.current;
      const recorder = mediaRecorderRef.current;

      if (!audioContext || recorder?.state !== "recording") {
        return;
      }

      try {
        await resumeAudioContextIfNeeded(audioContext);
      } catch (error) {
        logRecorderError(
          "Unable to resume audio context after tab visibility change",
          error,
        );
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    function handleClinicalNoteUpdate(payload = {}) {
      const eventData = payload?.data ?? {};
      const treatmentId = activeTreatmentIdRef.current;
      const runId = recorderRunIdRef.current;

      if (
        recorderStateRef.current !== RECORDER_STATE.SUMMARIZING ||
        !treatmentId
      ) {
        return;
      }

      if (eventData?.status === "completed") {
        resolveSummarySuccess({
          responseData: eventData,
          runId,
          source: "websocket",
          treatmentId,
        });
        return;
      }

      if (eventData?.status === "failed") {
        resolveSummaryFailure({
          detail: eventData?.detail,
          message: eventData?.message,
          runId,
          source: "websocket",
        });
      }
    }

    const unsubscribe = realtimeClient.subscribe(
      "clinical_note_update",
      handleClinicalNoteUpdate,
      { emitCachedPayload: false },
    );

    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateRecorderState(nextState) {
    recorderStateRef.current = nextState;

    if (!isMountedRef.current) {
      return;
    }

    setRecorderState(nextState);

    const treatmentId = activeTreatmentIdRef.current;
    if (treatmentId) {
      if (nextState === RECORDER_STATE.RECORDING) {
        sessionStorage.setItem(`presscribe_recorder_state_${treatmentId}`, "recording");
      } else if (nextState === RECORDER_STATE.PAUSED) {
        sessionStorage.setItem(`presscribe_recorder_state_${treatmentId}`, "paused");
      } else if (nextState === RECORDER_STATE.COMPLETED || nextState === RECORDER_STATE.IDLE) {
        sessionStorage.removeItem(`presscribe_recorder_state_${treatmentId}`);
      }
    }
  }

  function updateErrorMessage(nextMessage) {
    if (!isMountedRef.current) {
      return;
    }

    setErrorMessage(nextMessage);
  }

  function updateLatestSummaryResult(nextResult) {
    if (!isMountedRef.current) {
      return;
    }

    setLatestSummaryResult(nextResult);
  }

  function updateClinicalNoteSettings(nextSettings) {
    if (!isMountedRef.current) {
      return;
    }

    setClinicalNoteSettings(nextSettings);
  }

  function buildClinicalNoteSettings(note = {}) {
    const style = String(note?.style || "").trim();
    const language = String(note?.language || "").trim();

    if (!style || !language) {
      return null;
    }

    return {
      summaryStyle: style,
      summaryLanguage: language,
    };
  }

  function buildRecorderError(message, error) {
    if (error instanceof Error) {
      return error;
    }

    const { detail: errorDetail, message: errorMessage } =
      normalizeRecorderErrorDetails(error);
    return new Error(errorDetail || errorMessage || message || "Presscribe recorder error");
  }

  function logRecorderError(message, error, context = {}) {
    console.error(message, error);
    const {
      detail: errorDetail,
      message: errorMessage,
      name: errorName,
    } = normalizeRecorderErrorDetails(error);

    Sentry.withScope((scope) => {
      scope.setTag("feature", "presscribe");
      scope.setLevel("error");
      scope.setContext("presscribe", {
        activeTreatmentId: activeTreatmentIdRef.current,
        errorDetail,
        errorMessage,
        errorName,
        message,
        recorderState: recorderStateRef.current,
        ...context,
      });
      Sentry.captureException(buildRecorderError(message, error));
    });
  }

  function clearSummaryPolling() {
    if (pollingStartTimeoutRef.current) {
      clearTimeout(pollingStartTimeoutRef.current);
      pollingStartTimeoutRef.current = null;
    }

    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }

    isPollingInFlightRef.current = false;
  }

  function clearRecorderRotationTimeout() {
    if (recorderRotationTimeoutRef.current) {
      clearTimeout(recorderRotationTimeoutRef.current);
      recorderRotationTimeoutRef.current = null;
    }
  }

  function beginRotation(runId) {
    rotationPromiseRunIdRef.current = runId;
    rotationPromiseResolveRef.current = null;
    rotationPromiseRef.current = new Promise((resolve) => {
      rotationPromiseResolveRef.current = resolve;
    });
  }

  function endRotation(runId) {
    if (rotationPromiseRunIdRef.current !== runId) {
      return;
    }

    const resolve = rotationPromiseResolveRef.current;
    rotationPromiseRunIdRef.current = 0;
    rotationPromiseRef.current = null;
    rotationPromiseResolveRef.current = null;
    if (typeof resolve === "function") {
      resolve();
    }
  }

  async function waitForRotationIfNeeded(runId) {
    const rotationPromise = rotationPromiseRef.current;
    if (
      recorderStopModeRef.current !== "rotating" ||
      !rotationPromise ||
      rotationPromiseRunIdRef.current !== runId
    ) {
      return;
    }

    try {
      await rotationPromise;
    } catch {
      // ignore
    }
  }

  function resetSummaryResolutionState() {
    activeSummaryResolutionRunIdRef.current = 0;
    summaryResolutionSourceRef.current = "";
  }

  function beginSummaryResolution(runId) {
    activeSummaryResolutionRunIdRef.current = runId;
    summaryResolutionSourceRef.current = "";
  }

  function claimSummaryResolution(runId, source) {
    if (
      runId !== recorderRunIdRef.current ||
      recorderStateRef.current !== RECORDER_STATE.SUMMARIZING ||
      activeSummaryResolutionRunIdRef.current !== runId ||
      summaryResolutionSourceRef.current
    ) {
      return false;
    }

    summaryResolutionSourceRef.current = source;
    return true;
  }

  function resolveSummarySuccess({ responseData, runId, source, treatmentId }) {
    if (!claimSummaryResolution(runId, source)) {
      return false;
    }

    clearSummaryPolling();
    updateErrorMessage("");
    updateClinicalNoteSettings(buildClinicalNoteSettings(responseData));
    updateLatestSummaryResult({
      ...responseData,
      completedAt: Date.now(),
      treatmentId: String(treatmentId),
    });
    updateRecorderState(RECORDER_STATE.COMPLETED);
    return true;
  }

  function resolveSummaryFailure({ detail, message, runId, source }) {
    if (!claimSummaryResolution(runId, source)) {
      return false;
    }

    clearSummaryPolling();
    logRecorderError(
      "Presscribe summary failed",
      {
        detail,
        message,
        status: "failed",
      },
      { source },
    );
    setRecorderError(SUMMARY_FAILURE_MESSAGE);
    return true;
  }

  function resetRecorderSessionState() {
    mimeTypeRef.current = "";
    bufferedAudioChunksRef.current = [];
    currentSegmentStartedAtRef.current = 0;
    chunkOrderRef.current = 1;
    pendingUploadTasksRef.current = [];
    pendingFinalizeChunkRef.current = null;
    pendingDirectSummarizeRef.current = false;
    activeUploadCountRef.current = 0;
    uploadQueuePausedRef.current = false;
    uploadProcessorPromiseRef.current = null;
    recorderStopModeRef.current = "idle";
    pendingRestartResetPromiseRef.current = null;
    pendingRestartResetRunIdRef.current = 0;
    pendingRecordingProgressRestorePromiseRef.current = null;
    pendingRecordingProgressRestoreRunIdRef.current = 0;
    uploadedDurationSecondsRef.current = 0;
    updateClinicalNoteSettings(null);
    resetSummaryResolutionState();
    clearSummaryPolling();
    clearRecorderRotationTimeout();
  }

  function setRecorderError(message = SUMMARY_FAILURE_MESSAGE) {
    updateErrorMessage(message);
    updateRecorderState(
      activeTreatmentIdRef.current
        ? RECORDER_STATE.PAUSED
        : RECORDER_STATE.ERROR,
    );
  }

  async function handleRecorderFailure(
    message,
    error,
    { cleanup = false } = {},
  ) {
    logRecorderError(message, error);
    clearSummaryPolling();

    if (cleanup) {
      await cleanupRecordingSession();
    }

    setRecorderError(resolveRecorderUserMessage(error));
  }

  function buildChunkFilenamePrefix(treatmentId) {
    return `treatment-${treatmentId || "presscribe-session"}`;
  }

  function buildChunkUploadTask({
    blob,
    durationMs,
    chunkIndex,
    isLastChunk = false,
    runId,
    mimeType,
  }) {
    const treatmentId = activeTreatmentIdRef.current;
    const audioFile = buildAudioChunkFile(blob, {
      cuid: buildChunkFilenamePrefix(treatmentId),
      mimeType: mimeType || blob?.type || mimeTypeRef.current,
      order: chunkIndex,
    });

    if (!audioFile.size) {
      return null;
    }

    const normalizedDurationSeconds = Math.max(
      1,
      Math.round((Number(durationMs) || 0) / 1000),
    );
    const uploadedDurationSeconds =
      uploadedDurationSecondsRef.current + normalizedDurationSeconds;
    uploadedDurationSecondsRef.current = uploadedDurationSeconds;

    return {
      audioFile,
      chunkIndex,
      durationMs,
      isLastChunk,
      runId,
      treatmentId,
      uploadedDurationSeconds,
    };
  }

  async function waitForPendingRestartReset(runId) {
    const resetPromise = pendingRestartResetPromiseRef.current;

    if (!resetPromise || pendingRestartResetRunIdRef.current !== runId) {
      return;
    }

    try {
      await resetPromise;
    } finally {
      if (
        pendingRestartResetRunIdRef.current === runId &&
        pendingRestartResetPromiseRef.current === resetPromise
      ) {
        pendingRestartResetPromiseRef.current = null;
        pendingRestartResetRunIdRef.current = 0;
      }
    }
  }

  async function waitForPendingRecordingProgressRestore(runId) {
    const restorePromise = pendingRecordingProgressRestorePromiseRef.current;

    if (
      !restorePromise ||
      pendingRecordingProgressRestoreRunIdRef.current !== runId
    ) {
      return;
    }

    try {
      const restoredProgress = await restorePromise;
      const restoredChunkOrder = restoredProgress?.nextChunkIndex;
      const restoredUploadedDurationSeconds =
        restoredProgress?.uploadedDurationSeconds;
      if (
        runId === recorderRunIdRef.current &&
        Number.isFinite(restoredChunkOrder) &&
        restoredChunkOrder > 0
      ) {
        chunkOrderRef.current = Math.max(
          chunkOrderRef.current,
          restoredChunkOrder,
        );
      }
      if (
        runId === recorderRunIdRef.current &&
        Number.isFinite(restoredUploadedDurationSeconds) &&
        restoredUploadedDurationSeconds >= 0
      ) {
        uploadedDurationSecondsRef.current = Math.max(
          uploadedDurationSecondsRef.current,
          restoredUploadedDurationSeconds,
        );
        restoreElapsedTimer(
          Math.max(elapsedSeconds, restoredUploadedDurationSeconds),
        );
      }
    } finally {
      if (
        pendingRecordingProgressRestoreRunIdRef.current === runId &&
        pendingRecordingProgressRestorePromiseRef.current === restorePromise
      ) {
        pendingRecordingProgressRestorePromiseRef.current = null;
        pendingRecordingProgressRestoreRunIdRef.current = 0;
      }
    }
  }

  async function uploadChunkTask(task) {
    const {
      audioFile,
      chunkIndex,
      isLastChunk,
      runId,
      treatmentId,
      uploadedDurationSeconds,
    } = task;

    if (!treatmentId || runId !== recorderRunIdRef.current) {
      return null;
    }

    if (!audioFile?.size) {
      return null;
    }

    await waitForPendingRestartReset(runId);

    if (!treatmentId || runId !== recorderRunIdRef.current) {
      return null;
    }

    return uploadAudioChunk({
      treatmentId,
      audioFile,
      chunkIndex,
      uploadedDurationSeconds,
      isLastChunk,
    });
  }

  async function runUploadProcessor({ pauseOnFailure }) {
    if (uploadProcessorPromiseRef.current) {
      return uploadProcessorPromiseRef.current;
    }

    uploadProcessorPromiseRef.current = (async () => {
      let firstError = null;
      let shouldStopLaunching = false;

      async function worker() {
        while (true) {
          if (shouldStopLaunching || uploadQueuePausedRef.current) {
            return;
          }

          const task = pendingUploadTasksRef.current.shift();
          if (!task) {
            return;
          }

          activeUploadCountRef.current += 1;

          try {
            await uploadChunkTask(task);
          } catch (error) {
            // rolling upload ห้ามทำเสียงหาย จึงเอา task กลับไปเข้าหัวคิวเสมอ
            pendingUploadTasksRef.current.unshift(task);
            firstError = firstError || error;
            shouldStopLaunching = true;
            if (pauseOnFailure) {
              uploadQueuePausedRef.current = true;
            }
          } finally {
            activeUploadCountRef.current = Math.max(
              0,
              activeUploadCountRef.current - 1,
            );
          }
        }
      }

      const workerCount = Math.min(
        AUDIO_UPLOAD_MAX_CONCURRENCY,
        Math.max(1, pendingUploadTasksRef.current.length),
      );

      await Promise.allSettled(
        Array.from({ length: workerCount }, () => worker()),
      );

      if (firstError && !pauseOnFailure) {
        throw firstError;
      }
    })().finally(() => {
      uploadProcessorPromiseRef.current = null;
    });

    return uploadProcessorPromiseRef.current;
  }

  function schedulePendingUploadRetry() {
    if (!pendingUploadTasksRef.current.length) {
      return;
    }

    uploadQueuePausedRef.current = false;

    void runUploadProcessor({ pauseOnFailure: true }).catch((error) => {
      logRecorderError("Unable to upload rolling audio chunk", error);
    });
  }

  async function retryPendingUploadsOnce(runId) {
    if (uploadProcessorPromiseRef.current) {
      await uploadProcessorPromiseRef.current.catch(() => null);
    }

    if (runId !== recorderRunIdRef.current) {
      return;
    }

    const retryTasks = pendingUploadTasksRef.current;
    pendingUploadTasksRef.current = [];
    uploadQueuePausedRef.current = false;
    const failedTasks = [];

    for (const task of retryTasks) {
      if (runId !== recorderRunIdRef.current) {
        return;
      }

      try {
        await uploadChunkTask(task);
      } catch (error) {
        failedTasks.push(task);
        logRecorderError("Unable to retry pending audio chunk", error);
      }
    }

    pendingUploadTasksRef.current = [
      ...failedTasks,
      ...pendingUploadTasksRef.current,
    ];
  }

  function runBufferedAudioMutation(operation) {
    const previousMutation = bufferedAudioMutationPromiseRef.current.catch(
      () => {},
    );
    const currentMutation = previousMutation.then(operation);
    bufferedAudioMutationPromiseRef.current = currentMutation.catch(() => {});
    return currentMutation;
  }

  async function flushBufferedAudio({ runId, forceFinal = false } = {}) {
    while (runId === recorderRunIdRef.current) {
      await waitForPendingRecordingProgressRestore(runId);

      if (runId !== recorderRunIdRef.current) {
        return;
      }

      const uploadTask = await runBufferedAudioMutation(async () => {
        if (runId !== recorderRunIdRef.current) {
          return null;
        }

        const bufferedChunks = bufferedAudioChunksRef.current;

        if (!bufferedChunks.length) {
          return null;
        }

        const bufferedDurationMs = getBufferedDurationMs(bufferedChunks);
        if (!forceFinal && bufferedDurationMs < AUDIO_UPLOAD_CHUNK_TARGET_MS) {
          return null;
        }

        const cutDurationMs = forceFinal
          ? bufferedDurationMs
          : findQuietAudioCutDurationMs(bufferedChunks);

        if (cutDurationMs < 0) {
          return null;
        }

        if (
          !forceFinal &&
          cutDurationMs < AUDIO_UPLOAD_CHUNK_CUT_WINDOW_START_MS
        ) {
          return null;
        }

        const chunkIndex = chunkOrderRef.current;
        const { remainingChunks, uploadBlob, uploadDurationMs } =
          await splitBufferedAudioAtDuration(bufferedChunks, cutDurationMs);

        if (!uploadDurationMs) {
          return null;
        }

        // ตัด buffer ออกจากหน่วยความจำหลัง split สำเร็จแล้วเท่านั้น เพื่อกันเสียงตกหล่น
        chunkOrderRef.current += 1;
        bufferedAudioChunksRef.current = remainingChunks;

        return buildChunkUploadTask({
          blob: uploadBlob,
          chunkIndex,
          durationMs: uploadDurationMs,
          isLastChunk: false,
          mimeType: uploadBlob.type || "audio/wav",
          runId,
        });
      });

      if (!uploadTask) {
        return;
      }

      pendingUploadTasksRef.current.push(uploadTask);
      schedulePendingUploadRetry();

      forceFinal = false;
    }
  }

  async function buildBufferedChunkTask(runId, { isLastChunk = false } = {}) {
    await waitForPendingRecordingProgressRestore(runId);

    if (runId !== recorderRunIdRef.current) {
      return null;
    }

    return runBufferedAudioMutation(async () => {
      if (runId !== recorderRunIdRef.current) {
        return null;
      }

      const bufferedChunks = bufferedAudioChunksRef.current;
      if (!bufferedChunks.length) {
        return null;
      }

      const chunkIndex = chunkOrderRef.current;
      const chunkBlob = await buildCombinedAudioChunkBlob(bufferedChunks);
      const durationMs = getBufferedDurationMs(bufferedChunks);

      // clear buffer หลังรวมไฟล์สำเร็จแล้ว เพื่อให้ pause/stop ไม่ทำเสียงหายระหว่าง encode
      bufferedAudioChunksRef.current = [];

      if (!chunkBlob.size) {
        return null;
      }

      chunkOrderRef.current += 1;

      return buildChunkUploadTask({
        blob: chunkBlob,
        chunkIndex,
        durationMs,
        isLastChunk,
        mimeType: chunkBlob.type || "audio/wav",
        runId,
      });
    });
  }

  function handleRecorderDataAvailable(event, runId) {
    if (runId !== recorderRunIdRef.current || !event.data?.size) {
      return;
    }

    const now = Date.now();
    const segmentStartedAt =
      currentSegmentStartedAtRef.current || now - RECORDER_TIMESLICE_MS;
    const durationMs = Math.max(1, now - segmentStartedAt);
    currentSegmentStartedAtRef.current = now;

    const audioLevelBatch = consumeAudioLevelSampleBatchUntil(now, {
      durationMs,
      segmentStartedAt,
    });

    // เก็บทั้ง blob และ sample ระดับเสียง เพื่อหา quiet-cut แบบละเอียดตอนครบ 25 วิ
    bufferedAudioChunksRef.current.push({
      blob: event.data,
      durationMs,
      averageLevel: audioLevelBatch.averageLevel,
      levelSamples: audioLevelBatch.samples,
    });

    void flushBufferedAudio({ runId }).catch((error) => {
      logRecorderError("Unable to queue rolling audio chunk", error);
    });
  }

  function handleRecorderResumed(runId) {
    if (runId !== recorderRunIdRef.current) {
      return;
    }

    resumeElapsedTimer();
    startAudioLevelMonitoring(analyserRef.current);
    updateRecorderState(RECORDER_STATE.RECORDING);
  }

  function handleRecorderStopped(runId) {
    if (runId !== recorderRunIdRef.current) {
      return;
    }

    if (recorderStopModeRef.current === "rotating") {
      return;
    }

    pauseElapsedTimer();
    stopAudioLevelMonitoring();
  }

  function handleRecorderRuntimeError(runId) {
    if (runId !== recorderRunIdRef.current) {
      return;
    }

    clearRecorderRotationTimeout();
    pauseElapsedTimer();
    stopAudioLevelMonitoring();
    clearSummaryPolling();
    void cleanupRecordingSession().catch((error) => {
      logRecorderError(
        "Unable to clean up recording session after runtime error",
        error,
      );
    });
    setRecorderError();
  }

  function attachMediaRecorderListeners(mediaRecorder, runId) {
    mediaRecorder.addEventListener("dataavailable", (event) => {
      handleRecorderDataAvailable(event, runId);
    });
    mediaRecorder.addEventListener("stop", () => {
      handleRecorderStopped(runId);
    });
    mediaRecorder.addEventListener("error", () => {
      handleRecorderRuntimeError(runId);
    });
  }

  function scheduleRecorderRotation(runId) {
    clearRecorderRotationTimeout();

    recorderRotationTimeoutRef.current = window.setTimeout(() => {
      recorderRotationTimeoutRef.current = null;
      void rotateRecorderSegment(runId).catch((error) => {
        void handleRecorderFailure(
          "Unable to rotate recording segment",
          error,
          { cleanup: true },
        );
      });
    }, RECORDER_TIMESLICE_MS);
  }

  function createMediaRecorder(stream) {
    const recorderOptions = mimeTypeRef.current
      ? { mimeType: mimeTypeRef.current }
      : undefined;
    const mediaRecorder = new window.MediaRecorder(stream, recorderOptions);

    if (!mimeTypeRef.current) {
      mimeTypeRef.current = mediaRecorder.mimeType || "audio/webm";
    }

    return mediaRecorder;
  }

  async function startRecorderSegment(runId) {
    if (runId !== recorderRunIdRef.current) {
      return;
    }

    const stream = mediaStreamRef.current;
    if (!stream) {
      throw new Error("Missing media stream");
    }

    const mediaRecorder = createMediaRecorder(stream);
    mediaRecorderRef.current = mediaRecorder;
    currentSegmentStartedAtRef.current = Date.now();
    attachMediaRecorderListeners(mediaRecorder, runId);
    mediaRecorder.start();
    scheduleRecorderRotation(runId);
  }

  async function rotateRecorderSegment(runId) {
    if (runId !== recorderRunIdRef.current) {
      return;
    }

    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state !== "recording") {
      return;
    }

    beginRotation(runId);
    recorderStopModeRef.current = "rotating";
    mediaRecorderRef.current = null;

    try {
      await stopMediaRecorder(recorder);
    } finally {
      if (recorderStopModeRef.current === "rotating") {
        recorderStopModeRef.current = "idle";
      }
      endRotation(runId);
    }

    if (!isMountedRef.current || runId !== recorderRunIdRef.current) {
      return;
    }

    if (
      recorderStopModeRef.current !== "idle" ||
      recorderStateRef.current !== RECORDER_STATE.RECORDING
    ) {
      return;
    }

    await startRecorderSegment(runId);
  }

  async function cleanupRecordingSession() {
    clearRecorderRotationTimeout();
    pauseElapsedTimer();
    stopAudioLevelMonitoring();

    const recorder = mediaRecorderRef.current;
    mediaRecorderRef.current = null;

    try {
      recorderStopModeRef.current = "stopping";
      await stopMediaRecorder(recorder);
    } catch (error) {
      logRecorderError("Unable to stop media recorder", error);
    } finally {
      if (recorderStopModeRef.current === "stopping") {
        recorderStopModeRef.current = "idle";
      }
    }

    const stream = mediaStreamRef.current;
    mediaStreamRef.current = null;
    stream?.getTracks().forEach((track) => track.stop());

    if (sourceNodeRef.current) {
      sourceNodeRef.current.disconnect();
      sourceNodeRef.current = null;
    }

    analyserRef.current = null;

    const audioContext = audioContextRef.current;
    audioContextRef.current = null;
    if (audioContext && audioContext.state !== "closed") {
      try {
        await audioContext.close();
      } catch (error) {
        logRecorderError("Unable to close audio context", error);
      }
    }
  }

  cleanupRecordingSessionRef.current = cleanupRecordingSession;

  async function startRecordingSession({
    treatmentId,
    resetBeforeUploadPromise = null,
    restoreRecordingProgressPromise = null,
  } = {}) {
    if (!isAudioRecordingSupported()) {
      updateErrorMessage("");
      updateRecorderState(RECORDER_STATE.UNSUPPORTED);
      return;
    }

    const resolvedTreatmentId = normalizeTreatmentId(
      treatmentId ?? activeTreatmentIdRef.current,
    );

    if (!resolvedTreatmentId) {
      setRecorderError();
      return;
    }

    const runId = recorderRunIdRef.current + 1;
    recorderRunIdRef.current = runId;
    activeTreatmentIdRef.current = resolvedTreatmentId;

    updateErrorMessage("");
    updateLatestSummaryResult(null);
    updateRecorderState(RECORDER_STATE.INITIALIZING);
    resetElapsedTimer();
    stopAudioLevelMonitoring();
    resetRecorderSessionState();
    pendingRestartResetPromiseRef.current = resetBeforeUploadPromise;
    pendingRestartResetRunIdRef.current = resetBeforeUploadPromise ? runId : 0;
    pendingRecordingProgressRestorePromiseRef.current =
      restoreRecordingProgressPromise;
    pendingRecordingProgressRestoreRunIdRef.current =
      restoreRecordingProgressPromise ? runId : 0;

    await cleanupRecordingSession();

    try {
      const stream = await navigator.mediaDevices.getUserMedia(
        MICROPHONE_CONSTRAINTS,
      );

      if (!isMountedRef.current || runId !== recorderRunIdRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      mediaStreamRef.current = stream;

      const { analyser, audioContext, sourceNode } =
        await createAudioProcessingGraph(stream);
      analyserRef.current = analyser;
      audioContextRef.current = audioContext;
      sourceNodeRef.current = sourceNode;

      const preferredMimeType = getPreferredMimeType();
      if (!preferredMimeType) {
        await cleanupRecordingSession();
        updateRecorderState(RECORDER_STATE.UNSUPPORTED);
        return;
      }

      mimeTypeRef.current = preferredMimeType;
      await startRecorderSegment(runId);

      resumeElapsedTimer();
      void waitForPendingRecordingProgressRestore(runId).catch((error) => {
        logRecorderError("Unable to restore recording progress", error);
      });
      startAudioLevelMonitoring(analyser);
      updateRecorderState(RECORDER_STATE.RECORDING);
    } catch (error) {
      await handleRecorderFailure("Unable to start recording session", error, {
        cleanup: true,
      });
      resetElapsedTimer();
    }
  }

  async function pollClinicalNoteStatus(runId, treatmentId) {
    if (isPollingInFlightRef.current) {
      return;
    }

    isPollingInFlightRef.current = true;

    try {
      const response = await fetchClinicalNote(treatmentId);

      if (!isMountedRef.current || runId !== recorderRunIdRef.current) {
        return;
      }

      if (response?.status === "failed") {
        resolveSummaryFailure({
          runId,
          source: "polling",
        });
        return;
      }

      if (response?.status === "completed") {
        resolveSummarySuccess({
          responseData: response,
          runId,
          source: "polling",
          treatmentId,
        });
      }
    } catch (error) {
      await handleRecorderFailure(
        "Unable to poll clinical note summary",
        error,
      );
    } finally {
      isPollingInFlightRef.current = false;
    }
  }

  function beginClinicalNotePolling(runId, treatmentId) {
    clearSummaryPolling();

    pollingTimeoutRef.current = window.setTimeout(() => {
      pollingTimeoutRef.current = null;
      resolveSummaryFailure({
        detail: "Clinical note summary polling timed out",
        message: `Clinical note summary did not complete within ${CLINICAL_NOTE_POLL_TIMEOUT_MS}ms`,
        runId,
        source: "polling_timeout",
      });
    }, CLINICAL_NOTE_POLL_TIMEOUT_MS);

    pollingStartTimeoutRef.current = window.setTimeout(() => {
      pollingStartTimeoutRef.current = null;

      void pollClinicalNoteStatus(runId, treatmentId);

      pollingIntervalRef.current = window.setInterval(() => {
        void pollClinicalNoteStatus(runId, treatmentId);
      }, CLINICAL_NOTE_POLL_INTERVAL_MS);
    }, CLINICAL_NOTE_POLL_DELAY_MS);
  }

  async function finalizeSummaryRequest(runId, treatmentId) {
    // ตอนกดสรุปให้ retry queue เดิมแบบ best-effort หนึ่งครั้ง แล้วไปต่อแม้บาง chunk ยัง fail
    await retryPendingUploadsOnce(runId);

    if (runId !== recorderRunIdRef.current) {
      return;
    }

    await waitForPendingRestartReset(runId);

    if (runId !== recorderRunIdRef.current) {
      return;
    }

    if (pendingFinalizeChunkRef.current) {
      const finalChunkTask = pendingFinalizeChunkRef.current;
      pendingFinalizeChunkRef.current = null;
      pendingDirectSummarizeRef.current = false;
      try {
        await uploadChunkTask(finalChunkTask);
        return;
      } catch (error) {
        pendingFinalizeChunkRef.current = finalChunkTask;
        logRecorderError("Unable to upload final audio chunk", error);
        pendingDirectSummarizeRef.current = true;
      }
    }

    if (pendingDirectSummarizeRef.current) {
      await summarizeClinicalNote(treatmentId);
      pendingDirectSummarizeRef.current = false;
    }
  }

  async function handleRestartRecording() {
    const treatmentId = activeTreatmentIdRef.current;
    const resetBeforeUploadPromise = treatmentId
      ? resetClinicalNoteAudio(treatmentId).catch((error) => {
          if (
            error?.status === 400 &&
            error?.detail === "No active clinical note to reset"
          ) {
            return null;
          }

          logRecorderError("Unable to reset clinical note audio", error);
          throw error;
        })
      : null;

    await startRecordingSession({
      resetBeforeUploadPromise,
      treatmentId,
    });
  }

  async function continueRecordingSession() {
    if (!isAudioRecordingSupported()) {
      updateRecorderState(RECORDER_STATE.UNSUPPORTED);
      return;
    }

    const treatmentId = activeTreatmentIdRef.current;
    if (!treatmentId) {
      setRecorderError();
      return;
    }

    const runId = recorderRunIdRef.current + 1;
    recorderRunIdRef.current = runId;
    clearSummaryPolling();
    pendingFinalizeChunkRef.current = null;
    pendingDirectSummarizeRef.current = false;
    updateErrorMessage("");
    updateRecorderState(RECORDER_STATE.INITIALIZING);

    await cleanupRecordingSession();

    try {
      const stream = await navigator.mediaDevices.getUserMedia(
        MICROPHONE_CONSTRAINTS,
      );

      if (!isMountedRef.current || runId !== recorderRunIdRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      mediaStreamRef.current = stream;

      const { analyser, audioContext, sourceNode } =
        await createAudioProcessingGraph(stream);
      analyserRef.current = analyser;
      audioContextRef.current = audioContext;
      sourceNodeRef.current = sourceNode;

      const preferredMimeType = getPreferredMimeType();
      if (!preferredMimeType) {
        await cleanupRecordingSession();
        updateRecorderState(RECORDER_STATE.UNSUPPORTED);
        return;
      }

      mimeTypeRef.current = preferredMimeType;
      await startRecorderSegment(runId);

      resumeElapsedTimer();
      startAudioLevelMonitoring(analyser);
      updateLatestSummaryResult(null);
      updateRecorderState(RECORDER_STATE.RECORDING);
    } catch (error) {
      await handleRecorderFailure(
        "Unable to continue recording session",
        error,
        { cleanup: true },
      );
    }
  }

  function extractClinicalNoteProgress(response) {
    const lastChunkIndex = Number.parseInt(response?.last_chunk_index, 10);
    const uploadedDurationSeconds = Number.parseInt(
      response?.uploaded_duration_seconds,
      10,
    );

    return {
      lastChunkIndex:
        Number.isFinite(lastChunkIndex) && lastChunkIndex >= 0
          ? lastChunkIndex
          : null,
      uploadedDurationSeconds:
        Number.isFinite(uploadedDurationSeconds) && uploadedDurationSeconds >= 0
          ? uploadedDurationSeconds
          : null,
    };
  }

  function applyClinicalNoteProgress(progress) {
    if (!progress) {
      return;
    }

    const { lastChunkIndex, uploadedDurationSeconds } = progress;

    if (lastChunkIndex !== null) {
      chunkOrderRef.current = Math.max(chunkOrderRef.current, lastChunkIndex + 1);
    }

    if (uploadedDurationSeconds !== null) {
      uploadedDurationSecondsRef.current = Math.max(
        uploadedDurationSecondsRef.current,
        uploadedDurationSeconds,
      );
      restoreElapsedTimer(uploadedDurationSeconds);
    }
  }

  async function ensureRecorderStarted(options = {}) {
    if (recorderStateRef.current !== RECORDER_STATE.IDLE) {
      return;
    }

    const treatmentId = normalizeTreatmentId(
      options.treatmentId ?? activeTreatmentIdRef.current,
    );
    let restoreRecordingProgressPromise = null;

    if (options.resumeFromExistingChunkIndex && treatmentId) {
      let response = null;

      try {
        response = await fetchClinicalNote(treatmentId);
        updateClinicalNoteSettings(buildClinicalNoteSettings(response));
      } catch (error) {
        if (
          error?.status === 404 ||
          (error?.status === 400 &&
            error?.detail === "Clinical note has no cuid")
        ) {
          response = null;
        } else {
          logRecorderError(
            "Unable to restore last uploaded chunk index",
            error,
          );
          throw error;
        }
      }

      if (response?.status === "completed") {
        applyClinicalNoteProgress(extractClinicalNoteProgress(response));

        activeTreatmentIdRef.current = treatmentId;
        updateErrorMessage("");
        // Completed summary exists already; show completed UI without re-applying summary to treatment form.
        updateLatestSummaryResult(null);
        updateRecorderState(RECORDER_STATE.COMPLETED);
        return;
      }

      if (response?.status === "in_progress") {
        applyClinicalNoteProgress(extractClinicalNoteProgress(response));

        const runId = recorderRunIdRef.current + 1;
        recorderRunIdRef.current = runId;
        activeTreatmentIdRef.current = treatmentId;
        updateErrorMessage("");
        updateLatestSummaryResult(null);
        updateRecorderState(RECORDER_STATE.SUMMARIZING);
        beginSummaryResolution(runId);
        beginClinicalNotePolling(runId, treatmentId);
        return;
      }

      if (response?.status === "failed") {
        applyClinicalNoteProgress(extractClinicalNoteProgress(response));
        activeTreatmentIdRef.current = treatmentId;
        updateLatestSummaryResult(null);
        setRecorderError(SUMMARY_FAILURE_MESSAGE);
        return;
      }

      const progress = extractClinicalNoteProgress(response);
      const lastChunkIndex = progress.lastChunkIndex;
      const uploadedDurationSeconds = progress.uploadedDurationSeconds;

      const savedState = sessionStorage.getItem(`presscribe_recorder_state_${treatmentId}`);

      if (savedState === "recording") {
        restoreRecordingProgressPromise = Promise.resolve({
          nextChunkIndex:
            Number.isFinite(lastChunkIndex) && lastChunkIndex >= 0
              ? lastChunkIndex + 1
              : 1,
          uploadedDurationSeconds:
            Number.isFinite(uploadedDurationSeconds) &&
            uploadedDurationSeconds >= 0
              ? uploadedDurationSeconds
              : 0,
        });
      } else {
        if (
          (Number.isFinite(lastChunkIndex) && lastChunkIndex > 0) ||
          (Number.isFinite(uploadedDurationSeconds) && uploadedDurationSeconds > 0)
        ) {
          applyClinicalNoteProgress(progress);
          activeTreatmentIdRef.current = treatmentId;
          updateErrorMessage("");
          updateLatestSummaryResult(null);
          updateRecorderState(RECORDER_STATE.PAUSED);
          return;
        }

        restoreRecordingProgressPromise = Promise.resolve({
          nextChunkIndex:
            Number.isFinite(lastChunkIndex) && lastChunkIndex >= 0
              ? lastChunkIndex + 1
              : 1,
          uploadedDurationSeconds:
            Number.isFinite(uploadedDurationSeconds) &&
            uploadedDurationSeconds >= 0
              ? uploadedDurationSeconds
              : 0,
        });
      }
    }

    await startRecordingSession({
      ...options,
      restoreRecordingProgressPromise,
      treatmentId,
    });
  }

  async function handlePauseOrResumeRecording() {
    const runId = recorderRunIdRef.current;
    await waitForRotationIfNeeded(runId);
    const recorder = mediaRecorderRef.current;

    if (!recorder && recorderStateRef.current !== RECORDER_STATE.PAUSED) {
      return;
    }

    if (recorder?.state === "recording") {
      try {
        clearRecorderRotationTimeout();
        recorderStopModeRef.current = "pausing";
        mediaRecorderRef.current = null;
        await stopMediaRecorder(recorder);
        if (recorderStopModeRef.current === "pausing") {
          recorderStopModeRef.current = "idle";
        }
        const pauseChunkTask = await buildBufferedChunkTask(runId, {
          isLastChunk: false,
        });
        if (pauseChunkTask) {
          pendingUploadTasksRef.current.push(pauseChunkTask);
          schedulePendingUploadRetry();
        }
        // pause ต้องให้ UI ไปอยู่สถานะหยุดชั่วคราวทันที แม้ upload รอบนี้จะยัง retry อยู่
        pauseElapsedTimer();
        stopAudioLevelMonitoring();
        updateErrorMessage("");
        updateRecorderState(RECORDER_STATE.PAUSED);
      } catch (error) {
        await handleRecorderFailure("Unable to pause recording", error);
      }
      return;
    }

    if (recorderStateRef.current === RECORDER_STATE.PAUSED) {
      try {
        updateErrorMessage("");

        if (!mediaStreamRef.current) {
          await continueRecordingSession();
          return;
        }

        await startRecorderSegment(runId);
        handleRecorderResumed(runId);
      } catch (error) {
        await handleRecorderFailure("Unable to resume recording", error);
      }
    }
  }

  async function handleStopRecording() {
    const runId = recorderRunIdRef.current;
    await waitForRotationIfNeeded(runId);
    const recorder = mediaRecorderRef.current;
    const treatmentId = activeTreatmentIdRef.current;

    if (!treatmentId || (!recorder && chunkOrderRef.current === 1)) {
      return;
    }

    updateErrorMessage("");
    updateRecorderState(RECORDER_STATE.SUMMARIZING);
    beginSummaryResolution(runId);

    try {
      await cleanupRecordingSession();

      const finalChunkTask = await buildBufferedChunkTask(runId, {
        isLastChunk: true,
      });
      if (finalChunkTask) {
        pendingFinalizeChunkRef.current = finalChunkTask;
      }
      pendingDirectSummarizeRef.current = !pendingFinalizeChunkRef.current;

      await finalizeSummaryRequest(runId, treatmentId);

      beginClinicalNotePolling(runId, treatmentId);
    } catch (error) {
      await handleRecorderFailure("Unable to summarize recording", error, {
        cleanup: true,
      });
    }
  }

  async function handleSummarizeAgain() {
    const treatmentId = activeTreatmentIdRef.current;
    if (!treatmentId) {
      setRecorderError();
      return;
    }

    updateErrorMessage("");
    updateRecorderState(RECORDER_STATE.SUMMARIZING);
    pendingDirectSummarizeRef.current = !pendingFinalizeChunkRef.current;
    beginSummaryResolution(recorderRunIdRef.current);

    try {
      await finalizeSummaryRequest(recorderRunIdRef.current, treatmentId);
      beginClinicalNotePolling(recorderRunIdRef.current, treatmentId);
    } catch (error) {
      await handleRecorderFailure("Unable to summarize again", error);
    }
  }

  async function summarizeBySettings(
    treatmentId,
    { style, language, saveAsDefault = false } = {},
  ) {
    if (!treatmentId) {
      setRecorderError();
      return;
    }

    const runId = recorderRunIdRef.current;
    updateErrorMessage("");
    updateRecorderState(RECORDER_STATE.SUMMARIZING);
    beginSummaryResolution(runId);

    try {
      updateClinicalNoteSettings(buildClinicalNoteSettings({ style, language }));
      await summarizeClinicalNote(treatmentId, {
        style,
        language,
        save_as_default: saveAsDefault,
      });
      beginClinicalNotePolling(runId, treatmentId);
    } catch (error) {
      await handleRecorderFailure("Unable to summarize with settings", error);
    }
  }

  async function resetRecorder() {
    recorderRunIdRef.current += 1;
    clearSummaryPolling();
    resetRecorderSessionState();
    activeTreatmentIdRef.current = null;
    updateLatestSummaryResult(null);
    await cleanupRecordingSession();
    resetElapsedTimer();
    updateErrorMessage("");
    updateRecorderState(RECORDER_STATE.IDLE);
  }

  async function startFreshRecorderSession(options = {}) {
    await resetRecorder();
    await startRecordingSession(options);
  }

  return {
    audioIntensity,
    audioLevel,
    clinicalNoteSettings,
    elapsedSeconds,
    handleContinueRecording: continueRecordingSession,
    activeTreatmentId: activeTreatmentIdRef.current,
    ensureRecorderStarted,
    handlePauseOrResumeRecording,
    handleRestartRecording,
    handleStopRecording,
    handleSummarizeAgain,
    isRecorderVisible: recorderState !== RECORDER_STATE.IDLE,
    latestSummaryResult,
    recorderErrorMessage: errorMessage,
    recorderState,
    recorderStatusMeta,
    resetRecorder,
    startFreshRecorderSession,
    summarizeBySettings,
  };
}
